<template>
  <div class="rich-text-editor">
    <!-- Slim utility bar: only general actions live here — formatting is in
         the selection bubble, where the user's attention already is. -->
    <div v-if="editor" class="toolbar">
      <button
        type="button"
        class="toolbar-btn rte-tt"
        :disabled="!editor.can().undo()"
        :aria-label="t('messages.editor.undo')"
        :data-tt="t('messages.editor.undo')"
        @click="editor.chain().focus().undo().run()"
      >
        <span class="pi pi-undo text-sm"></span>
      </button>
      <button
        type="button"
        class="toolbar-btn rte-tt"
        :disabled="!editor.can().redo()"
        :aria-label="t('messages.editor.redo')"
        :data-tt="t('messages.editor.redo')"
        @click="editor.chain().focus().redo().run()"
      >
        <!-- primeicons has no redo glyph — mirror the undo arrow. -->
        <span class="pi pi-undo -scale-x-100 text-sm"></span>
      </button>

      <span class="toolbar-divider"></span>

      <button
        type="button"
        class="toolbar-btn rte-tt"
        :disabled="uploading"
        :aria-label="t('messages.editor.insertImage')"
        :data-tt="t('messages.editor.insertImage')"
        @click="fileInput?.click()"
      >
        <span v-if="!uploading" class="pi pi-image text-sm"></span>
        <span v-else class="pi pi-spinner pi-spin text-sm"></span>
      </button>
      <input
        ref="fileInput"
        type="file"
        class="hidden"
        accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
        multiple
        @change="onFilePick"
      />
      <button
        type="button"
        class="toolbar-btn rte-tt"
        :disabled="editor.isActive('table')"
        :aria-label="t('messages.editor.insertTable')"
        :data-tt="t('messages.editor.insertTable')"
        @click="insertTableAtSafePoint"
      >
        <span class="pi pi-table text-sm"></span>
      </button>

      <!-- Host-specific actions (e.g. the plannings card's EHR draft). -->
      <slot name="toolbar-extra"></slot>

      <span v-if="uploading" class="ml-2 text-xs text-surface-500">
        {{ t('messages.editor.uploading') }} {{ progress }}%
      </span>

      <div class="ml-auto">
        <EditorPhrasesDropdown
          :category="category"
          :name="name"
          :shared-pools="pools"
          :text-to-save="plainText"
          @insert="insertPhrase"
        />
      </div>
    </div>

    <!-- Selection bubble: formatting where the selection is. -->
    <BubbleMenu
      v-if="editor"
      :editor="editor"
      plugin-key="rteFormatMenu"
      :should-show="bubbleShouldShow"
      :update-delay="100"
      :options="{ placement: 'top', offset: 8 }"
    >
      <div class="bubble">
        <button
          type="button"
          class="bubble-btn rte-tt rte-tt-up font-bold"
          :class="{ 'is-active': editor.isActive('bold') }"
          :aria-label="t('messages.editor.bold')"
          :data-tt="t('messages.editor.bold')"
          @click="editor.chain().focus().toggleBold().run()"
        >
          B
        </button>
        <button
          type="button"
          class="bubble-btn rte-tt rte-tt-up font-serif italic"
          :class="{ 'is-active': editor.isActive('italic') }"
          :aria-label="t('messages.editor.italic')"
          :data-tt="t('messages.editor.italic')"
          @click="editor.chain().focus().toggleItalic().run()"
        >
          I
        </button>
        <button
          type="button"
          class="bubble-btn rte-tt rte-tt-up underline"
          :class="{ 'is-active': editor.isActive('underline') }"
          :aria-label="t('messages.editor.underline')"
          :data-tt="t('messages.editor.underline')"
          @click="editor.chain().focus().toggleUnderline().run()"
        >
          U
        </button>

        <span class="bubble-divider"></span>

        <button
          type="button"
          class="bubble-btn rte-tt rte-tt-up"
          :class="{ 'is-active': editor.isActive('bulletList') }"
          :aria-label="t('messages.editor.bulletList')"
          :data-tt="t('messages.editor.bulletList')"
          @click="editor.chain().focus().toggleBulletList().run()"
        >
          <span class="pi pi-list text-sm"></span>
        </button>
        <button
          type="button"
          class="bubble-btn rte-tt rte-tt-up text-xs font-semibold"
          :class="{ 'is-active': editor.isActive('orderedList') }"
          :aria-label="t('messages.editor.orderedList')"
          :data-tt="t('messages.editor.orderedList')"
          @click="editor.chain().focus().toggleOrderedList().run()"
        >
          1.
        </button>

        <span class="bubble-divider"></span>

        <button
          type="button"
          class="bubble-btn rte-tt rte-tt-up"
          :disabled="savingPhrase"
          :aria-label="t('messages.editor.savePhraseSelection')"
          :data-tt="t('messages.editor.savePhraseSelection')"
          @click="saveSelectionAsPhrase"
        >
          <span class="pi pi-bookmark text-sm"></span>
        </button>
      </div>
    </BubbleMenu>

    <TableMenu v-if="editor" :editor="editor" />

    <EditorContent
      :editor="editor"
      class="editor-content"
      :style="{ '--editor-min-height': minHeight + 'px' }"
    />
  </div>
</template>

<script setup>
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { BubbleMenu } from '@tiptap/vue-3/menus'
import { FileHandler } from '@tiptap/extension-file-handler'
import { TableKit } from '@tiptap/extension-table'
import { NodeSelection } from '@tiptap/pm/state'
import StarterKit from '@tiptap/starter-kit'
import { useToast } from 'primevue/usetoast'
import { computed, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EditorPhrasesDropdown from '@/components/editor/EditorPhrasesDropdown.vue'
import TableMenu from '@/components/editor/TableMenu.vue'
import { PlanningImage } from '@/components/editor/extensions/planningImage'
import { usePlanningImageUpload } from '@/composables/usePlanningImageUpload'
import { usePredefinedValues } from '@/composables/usePredefinedValues'

const props = defineProps({
  objectId: { type: [String, Number], required: true },
  // Suggestion-pool key for the phrases dropdown (same pools as
  // PredefinedValuesInputComponent for this field).
  category: { type: String, required: true },
  name: { type: String, required: true },
  minHeight: { type: Number, default: 160 },
})

// v-model: the Tiptap JSON document (null/undefined = empty).
const model = defineModel({ type: Object, default: null })

const { t } = useI18n()
const toast = useToast()

const fileInput = useTemplateRef('fileInput')

const { uploading, progress, uploadImages } = usePlanningImageUpload(() => props.objectId)

// One shared pool instance: the bubble's save-selection and the dropdown's
// list stay in sync.
const pools = usePredefinedValues(
  () => props.category,
  () => props.name,
)
const { savingPhrase } = pools

// The extension whitelist mirrors PlanningsDocConverter exactly — anything
// the editor can produce, the docx export can render.
const editor = useEditor({
  content: model.value ?? undefined,
  extensions: [
    StarterKit.configure({
      blockquote: false,
      code: false,
      codeBlock: false,
      heading: false,
      horizontalRule: false,
      link: false,
      strike: false,
    }),
    PlanningImage,
    // resizable: false — no resize decorations/handles, cheaper on old
    // devices, and the export uses fixed proportional columns anyway.
    TableKit.configure({ table: { resizable: false } }),
    FileHandler.configure({
      allowedMimeTypes: [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/heic',
        'image/heif',
      ],
      onDrop: (currentEditor, files, pos) => uploadAndInsert(files, pos),
      onPaste: (currentEditor, files) => uploadAndInsert(files, null),
    }),
  ],
  onUpdate: ({ editor: instance }) => {
    model.value = instance.getJSON()
  },
})

const plainText = computed(() => editor.value?.getText({ blockSeparator: '\n' }) ?? '')

// Formatting bubble: text selections only — not the caret, not an image click.
const bubbleShouldShow = ({ editor: instance, state }) => {
  const { selection } = state
  return instance.isEditable && !selection.empty && !(selection instanceof NodeSelection)
}

const saveSelectionAsPhrase = async () => {
  if (!editor.value) {
    return
  }
  const { state } = editor.value
  const text = state.doc.textBetween(state.selection.from, state.selection.to, '\n').trim()
  if (!text) {
    return
  }
  await pools.saveAsPhrase(text)
  toast.add({ severity: 'success', summary: t('messages.editor.phraseSaved'), life: 2500 })
}

// External model changes (a fresh server payload) sync in without emitting —
// only real user edits may trigger update:modelValue and thereby a save.
// The JSON diff guards against loops from our own onUpdate.
watch(model, value => {
  if (!editor.value || !value) {
    return
  }
  if (JSON.stringify(value) !== JSON.stringify(editor.value.getJSON())) {
    editor.value.commands.setContent(value, { emitUpdate: false })
  }
})

// Block insertions (image, table) must never destroy content: collapse any
// range selection to a point (insertContent over a selection REPLACES it —
// e.g. select-all + bold, then the image button), and escape the image
// caption so the new block lands AFTER the figure instead of inside/over it.
const blockInsertPos = () => {
  const { selection } = editor.value.state
  const { $from } = selection
  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name === 'planningImage') {
      return $from.after(depth)
    }
  }
  return selection.to
}

const insertTableAtSafePoint = () => {
  editor.value
    ?.chain()
    .focus()
    .setTextSelection(blockInsertPos())
    .insertTable({ rows: 4, cols: 2, withHeaderRow: false })
    .run()
}

const uploadAndInsert = async (files, pos) => {
  const photos = await uploadImages(files)
  if (!photos.length || !editor.value) {
    return
  }

  const nodes = photos.map(photo => ({
    type: 'planningImage',
    attrs: { photoId: photo.id, src: photo.url, size: 'm' },
  }))

  // Content may have shifted during the async upload — clamp both paths.
  const max = editor.value.state.doc.content.size
  const insertAt = Math.min(pos ?? blockInsertPos(), max)
  editor.value.chain().focus().insertContentAt(insertAt, nodes).run()
}

const onFilePick = async event => {
  const files = [...(event.target.files ?? [])]
  event.target.value = ''
  if (files.length) {
    await uploadAndInsert(files, null)
  }
}

const insertPhrase = label => {
  editor.value?.chain().focus().insertContent(label).run()
}
</script>

<style scoped>
  @reference '../../../css/tailwind.css';

  .rich-text-editor {
    @apply rounded-lg border border-surface-300 bg-white shadow-xs transition-[border-color,box-shadow] duration-150 dark:border-surface-600 dark:bg-surface-900;

    &:focus-within {
      @apply border-primary-400 ring-2 ring-primary-500/20;
    }
  }

  .toolbar {
    @apply flex items-center gap-0.5 rounded-t-lg border-b border-surface-200 bg-surface-50 px-2 py-1.5 dark:border-surface-700 dark:bg-surface-800;
  }

  /* :slotted — toolbar-extra buttons (host-provided) share the styling.
     Kept un-nested: `&`-nesting inside a `.x, :slotted(.x)` list breaks the
     scoped transform (the :slotted half loses its -s attribute → slotted
     buttons get no hover/disabled styling). Pseudo-classes must sit INSIDE
     the :slotted() parens for the same reason. */
  .toolbar-btn,
  :slotted(.toolbar-btn) {
    @apply flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-surface-500 transition-colors dark:text-surface-400;
  }

  .toolbar-btn:hover:not(:disabled),
  :slotted(.toolbar-btn:hover:not(:disabled)) {
    @apply bg-surface-200/70 text-surface-900 dark:bg-surface-700 dark:text-surface-100;
  }

  .toolbar-btn:disabled,
  :slotted(.toolbar-btn:disabled) {
    @apply cursor-not-allowed opacity-35;
  }

  .toolbar-divider {
    @apply mx-1 h-4 w-px bg-surface-200 dark:bg-surface-600;
  }

  /* Medium-style selection bubble: dark pill, light glyphs. */
  .bubble {
    @apply flex items-center gap-0.5 rounded-full bg-surface-900 px-1.5 py-1 shadow-xl ring-1 ring-black/20 dark:bg-surface-950 dark:ring-white/10;
    animation: bubble-pop 0.12s ease-out;
  }

  .bubble-btn {
    @apply flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-sm text-surface-300 transition-colors;

    &:hover:not(:disabled) {
      @apply bg-white/10 text-white;
    }

    &.is-active {
      @apply bg-white/15 text-primary-300;
    }

    &:disabled {
      @apply cursor-not-allowed opacity-40;
    }
  }

  .bubble-divider {
    @apply mx-0.5 h-4 w-px bg-white/20;
  }

  @keyframes bubble-pop {
    from {
      opacity: 0;
      transform: translateY(3px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .editor-content :deep(.ProseMirror) {
    min-height: var(--editor-min-height, 160px);
    padding: 0.625rem 0.875rem;
    line-height: 1.55;
    outline: none;

    p {
      margin: 0 0 0.25rem;
    }

    ul,
    ol {
      margin: 0 0 0.25rem;
      padding-left: 1.5rem;
    }

    ul {
      list-style-type: disc;
    }

    ol {
      list-style-type: decimal;
    }

    /* Editing affordance only — the export renders tables borderless, like
       the hand-authored key/value spec blocks. */
    table {
      width: 100%;
      table-layout: fixed;
      border-collapse: collapse;
      margin: 0.375rem 0;

      td,
      th {
        @apply border border-dashed border-surface-300 dark:border-surface-600;
        position: relative;
        padding: 0.125rem 0.5rem;
        vertical-align: top;

        > p {
          margin: 0;
        }
      }

      th {
        @apply bg-surface-50 font-semibold dark:bg-surface-800;
      }

      .selectedCell::after {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        @apply bg-primary-500/10;
      }
    }

    .tableWrapper {
      overflow-x: auto;
    }

    .planning-image-node.ProseMirror-selectednode img {
      @apply ring-2 ring-primary-500;
    }
  }
</style>

<!-- Shared, CSS-only tooltips for every editor action (toolbar, bubbles,
     image node view). Pure pseudo-element — zero JS, zero extra DOM until
     hover paints it, compositor-only transitions: as cheap as a tooltip can
     be on old devices. Unscoped on purpose (namespaced with rte-): the node
     view and floating menus render outside this component's scope. -->
<style>
  @reference '../../../css/tailwind.css';

  .rte-tt {
    position: relative;
  }

  .rte-tt::after {
    content: attr(data-tt);
    position: absolute;
    left: 50%;
    top: calc(100% + 7px);
    transform: translateX(-50%) translateY(-2px);
    white-space: nowrap;
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    font-style: normal;
    text-decoration: none;
    line-height: 1.3;
    letter-spacing: 0.01em;
    opacity: 0;
    pointer-events: none;
    z-index: 60;
    transition:
      opacity 0.12s ease,
      transform 0.12s ease;
    @apply bg-surface-900 text-surface-0 shadow-md dark:bg-surface-700;
  }

  .rte-tt:hover::after,
  .rte-tt:focus-visible::after {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
    transition-delay: 0.35s;
  }

  .rte-tt-up::after {
    top: auto;
    bottom: calc(100% + 7px);
    transform: translateX(-50%) translateY(2px);
  }

  .rte-tt-up:hover::after,
  .rte-tt-up:focus-visible::after {
    transform: translateX(-50%) translateY(0);
  }
</style>
